/**
 * 全局加载状态管理器
 * 统一管理应用的各种加载状态，提供更好的用户体验
 */

class LoadingManager {
  constructor() {
    this.loadingStates = new Map()
    this.globalLoading = false
    this.loadingQueue = []
    this.listeners = new Set()
  }

  /**
   * 开始加载
   */
  startLoading(key, options = {}) {
    const {
      message = '加载中...',
      type = 'spinner',
      priority = 'normal', // low, normal, high
      timeout = 30000 // 30秒超时
    } = options

    const loadingState = {
      key,
      message,
      type,
      priority,
      startTime: Date.now(),
      timeout
    }

    this.loadingStates.set(key, loadingState)

    // 设置超时自动结束
    if (timeout > 0) {
      setTimeout(() => {
        this.endLoading(key)
      }, timeout)
    }

    // 更新全局状态
    this.updateGlobalLoadingState()

    // 通知监听器
    this.notifyListeners()

    return key
  }

  /**
   * 结束加载
   */
  endLoading(key) {
    if (this.loadingStates.has(key)) {
      this.loadingStates.delete(key)
      this.updateGlobalLoadingState()
      this.notifyListeners()
    }
  }

  /**
   * 更新加载状态
   */
  updateLoading(key, updates) {
    if (this.loadingStates.has(key)) {
      const state = this.loadingStates.get(key)
      Object.assign(state, updates)
      this.notifyListeners()
    }
  }

  /**
   * 获取加载状态
   */
  getLoadingState(key) {
    return this.loadingStates.get(key) || null
  }

  /**
   * 获取所有加载状态
   */
  getAllLoadingStates() {
    return Array.from(this.loadingStates.values())
  }

  /**
   * 检查是否正在加载
   */
  isLoading(key) {
    return this.loadingStates.has(key)
  }

  /**
   * 获取全局加载状态
   */
  isGlobalLoading() {
    return this.globalLoading
  }

  /**
   * 获取当前优先级最高的加载状态
   */
  getCurrentLoadingState() {
    const states = Array.from(this.loadingStates.values())

    if (states.length === 0) {
      return null
    }

    // 按优先级排序
    const priorityOrder = { high: 3, normal: 2, low: 1 }
    states.sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority])

    return states[0]
  }

  /**
   * 更新全局加载状态
   */
  updateGlobalLoadingState() {
    this.globalLoading = this.loadingStates.size > 0
  }

  /**
   * 添加监听器
   */
  addListener(callback) {
    this.listeners.add(callback)
  }

  /**
   * 移除监听器
   */
  removeListener(callback) {
    this.listeners.delete(callback)
  }

  /**
   * 通知所有监听器
   */
  notifyListeners() {
    const currentState = this.getCurrentLoadingState()
    const globalState = this.isGlobalLoading()

    this.listeners.forEach(callback => {
      try {
        callback({
          currentState,
          globalState,
          allStates: this.getAllLoadingStates()
        })
      } catch (error) {
        console.error('Loading listener error:', error)
      }
    })
  }

  /**
   * 创建加载Promise包装器
   */
  wrapPromise(key, promise, options = {}) {
    this.startLoading(key, options)

    return promise
      .then(result => {
        this.endLoading(key)
        return result
      })
      .catch(error => {
        this.endLoading(key)
        throw error
      })
  }

  /**
   * 创建防抖加载
   */
  debounceLoading(key, fn, delay = 300, options = {}) {
    if (this.loadingStates.has(key)) {
      return this.loadingStates.get(key).promise
    }

    const promise = new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          this.startLoading(key, options)
          const result = await fn()
          this.endLoading(key)
          resolve(result)
        } catch (error) {
          this.endLoading(key)
          reject(error)
        } finally {
          // 清理promise引用
          if (this.loadingStates.has(key)) {
            delete this.loadingStates.get(key).promise
          }
        }
      }, delay)
    })

    // 存储promise引用用于去重
    if (this.loadingStates.has(key)) {
      this.loadingStates.get(key).promise = promise
    }

    return promise
  }

  /**
   * 显示全局加载遮罩
   */
  showGlobalLoading(message = '加载中...') {
    return this.startLoading('global', {
      message,
      type: 'overlay',
      priority: 'high'
    })
  }

  /**
   * 隐藏全局加载遮罩
   */
  hideGlobalLoading() {
    this.endLoading('global')
  }

  /**
   * 显示页面加载状态
   */
  showPageLoading(message = '正在加载页面...') {
    return this.startLoading('page', {
      message,
      type: 'page',
      priority: 'high'
    })
  }

  /**
   * 隐藏页面加载状态
   */
  hidePageLoading() {
    this.endLoading('page')
  }

  /**
   * 显示数据加载状态
   */
  showDataLoading(message = '正在加载数据...') {
    return this.startLoading('data', {
      message,
      type: 'data',
      priority: 'normal'
    })
  }

  /**
   * 隐藏数据加载状态
   */
  hideDataLoading() {
    this.endLoading('data')
  }

  /**
   * 获取加载统计信息
   */
  getStats() {
    const states = Array.from(this.loadingStates.values())
    const now = Date.now()

    return {
      totalLoading: states.length,
      globalLoading: this.globalLoading,
      averageDuration: states.length > 0
        ? states.reduce((sum, state) => sum + (now - state.startTime), 0) / states.length
        : 0,
      priorityBreakdown: {
        high: states.filter(s => s.priority === 'high').length,
        normal: states.filter(s => s.priority === 'normal').length,
        low: states.filter(s => s.priority === 'low').length
      },
      typeBreakdown: {
        spinner: states.filter(s => s.type === 'spinner').length,
        overlay: states.filter(s => s.type === 'overlay').length,
        page: states.filter(s => s.type === 'page').length,
        data: states.filter(s => s.type === 'data').length
      }
    }
  }

  /**
   * 清理所有加载状态
   */
  clearAll() {
    this.loadingStates.clear()
    this.globalLoading = false
    this.notifyListeners()
  }

  /**
   * 销毁管理器
   */
  destroy() {
    this.clearAll()
    this.listeners.clear()
  }
}

// 创建全局实例
const loadingManager = new LoadingManager()

// 开发环境下添加调试信息
if (process.env.NODE_ENV === 'development') {
  window.loadingManager = loadingManager
}

export default loadingManager